import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaPinterestP, FaFacebookF, FaTwitter, FaEnvelope } from 'react-icons/fa';
import FadeIn from '../components/common/FadeIn';
import SEO from '../components/common/SEO';
import { blogPosts } from '../data/blogData';
import { VscArrowSmallLeft, VscArrowSmallRight } from "react-icons/vsc";


const BlogPostDetail = () => {
  const { slug } = useParams();

  const post = blogPosts.find((p) => p.slug === slug) || blogPosts[1];

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: {
      '@type': 'Person',
      name: 'Princy Patel'
    },
    publisher: {
      '@type': 'Organization',
      name: 'SeaSalt Creative by Princy Patel',
      logo: {
        '@type': 'ImageObject',
        url: 'https://midnightblue-reindeer-515591.hostingersite.com/logo192.png'
      }
    }
  };

  return (
    <main className="w-full min-h-screen bg-[#f9f9f5] pt-[30px] md:pt-[50px] pb-24 text-[#1C1917]">
      <SEO
        title={post.title}
        description={post.excerpt}
        type="article"
        schemaData={blogSchema}
      />
      <article className="max-w-[760px] mx-auto px-4 sm:px-6">

        <FadeIn className="text-center mb-4">
          <span className="font-interDisplayMedium text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#8B8477] font-bold">
            {post.category} — {post.date}
          </span>
        </FadeIn>

        <FadeIn className="text-center mb-8 md:mb-12">
          <h1 className="font-saolDisplayItalic text-[32px] sm:text-[44px] md:text-[54px] font-normal leading-[1.15] text-[#1C1917] tracking-[0em]">
            {post.title}
          </h1>
        </FadeIn>

        <FadeIn className="mb-10 md:mb-14">
          <div className="w-full overflow-hidden bg-[#EFECE6] aspect-[4/3] sm:aspect-[16/11]">
            <img
              src={post.featuredImage}
              alt={`${post.title} - SeaSalt Creative Blog Post`}
              className="w-full h-full object-cover object-center block"
              loading="eager"
              decoding="async"
              width="900"
              height="600"
            />
          </div>
        </FadeIn>

        <div className="space-y-6 text-[13px] sm:text-[14px] md:text-[15px] font-interDisplayMedium leading-[1.8] text-[#3E3832] mb-12">
          {post.content && post.content.map((paragraph, index) => (
            <FadeIn key={index}>
              <p>{paragraph}</p>
            </FadeIn>
          ))}
        </div>

        {post.author && (
          <FadeIn className="mb-12">
            <div className="bg-[#EFECE6] p-6 sm:p-8 flex flex-col sm:flex-row items-center sm:items-start gap-6 border border-[#E5E0D8]">
              <img
                src={post.author.avatar}
                alt={post.author.name}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover flex-shrink-0"
                loading="lazy"
                decoding="async"
                width="96"
                height="96"
              />
              <div className="text-center sm:text-left">
                <h3 className="font-saolDisplayItalic text-[20px] sm:text-[24px] font-normal text-[#1C1917] mb-2">
                  {post.author.name}
                </h3>
                <p className="font-interDisplayMedium text-[12px] sm:text-[13px] leading-[1.7] text-[#555555]">
                  {post.author.bio}
                </p>
              </div>
            </div>
          </FadeIn>
        )}

        <FadeIn className="mb-14">
          <div className="flex flex-col items-start gap-2 border-t border-[#E8E2D8] pt-6">
            <span className="font-interDisplayMedium text-[10px] uppercase tracking-[0.2em] font-bold text-[#8B8477]">
              SHARE
            </span>
            <div className="flex items-center gap-4 text-[#3E3832]">
              <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest" className="hover:opacity-70 transition-opacity">
                <FaPinterestP className="w-4 h-4" />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:opacity-70 transition-opacity">
                <FaFacebookF className="w-4 h-4" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="hover:opacity-70 transition-opacity">
                <FaTwitter className="w-4 h-4" />
              </a>
              <a href="mailto:?subject=Check%20out%20this%20blog%20post" aria-label="Email" className="hover:opacity-70 transition-opacity">
                <FaEnvelope className="w-4 h-4" />
              </a>
            </div>
          </div>
        </FadeIn>

        {/* Previous / Next Post Pagination */}
        <FadeIn>
          <div className="border-t border-[#E8E2D8] pt-8 flex flex-col sm:flex-row items-center justify-between gap-6 text-[10px] sm:text-[11px] font-interDisplayMedium uppercase tracking-[0.12em]">

            {post.prevPost ? (
              <div className="flex flex-col items-center sm:items-start text-center sm:text-left max-w-xs">
                <span className="text-[#8B8477] font-bold mb-1">PREVIOUS POST</span>
                <Link
                  to={`/blog/${post.prevPost.slug}`}
                  className="text-[#1C1917] hover:text-[#8C8275] transition-colors leading-[1.4] flex gap-1"
                >
                  <VscArrowSmallLeft className="w-5 h-5 flex-shrink-0" />
                  <span>{post.prevPost.title}</span>
                </Link>
              </div>
            ) : <div />}

            {post.nextPost ? (
              <div className="flex flex-col items-center sm:items-end text-center sm:text-right max-w-xs">
                <span className="text-[#8B8477] font-bold mb-1">NEXT POST</span>
                <Link
                  to={`/blog/${post.nextPost.slug}`}
                  className="text-[#1C1917] hover:text-[#8C8275] transition-colors leading-[1.4] flex gap-1 justify-end"
                >
                  <span>{post.nextPost.title}</span>
                  <VscArrowSmallRight className="w-5 h-5 flex-shrink-0" />
                </Link>
              </div>
            ) : <div />}

          </div>
        </FadeIn>

      </article>
    </main>
  );
};

export default BlogPostDetail;
